import { afterResponseHooks, retryRequest } from '@afinius/api-client/client';
import { useLandlordAuthStore } from '@afinius/hooks/store/useLandlordAuthStore';
import { i18n } from '@afinius/i18n/index';
import { routes as authRoutes } from '@afinius/modules/auth/routes';
import { routes as tenantRoutes } from '@afinius/modules/tenant/routes';
import { defaultRouterOptions } from '@afinius/utilities/routing';
import { refreshLandlordToken } from '@afinius/utilities/silent-sso';
import { message } from 'ant-design-vue';
import {
  createRouter,
  createWebHistory,
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from 'vue-router';

import AppLayout from './modules/app/views/layouts/AppLayout/AppLayout.vue';

const REQUEST_ACCESS_ROUTE_NAMES = ['tenant.request-access', 'tenant.request-access-invite'];

const restrictGenericRoutes = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
): NavigationGuardReturn => {
  const authStore = useLandlordAuthStore();

  if (!authStore.isAuthenticated) {
    return { name: 'auth.login', query: { returnTo: to.fullPath } };
  }

  if (!authStore.hasTenantAccess && !REQUEST_ACCESS_ROUTE_NAMES.includes(to.name as string)) {
    return { name: 'tenant.request-access' };
  }

  return true;
};

export const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      component: AppLayout,
      children: [
        ...authRoutes,
        {
          path: '/',
          redirect: (to) => ({ name: 'auth.login', query: to.query }),
        },
      ],
    },
    {
      path: '/',
      component: AppLayout,
      beforeEnter: restrictGenericRoutes,
      children: [
        ...tenantRoutes,
        {
          path: '/',
          redirect: { name: 'tenant.index' },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: (to) => ({ name: 'auth.login', query: to.query }),
    },
  ],
  ...defaultRouterOptions,
});

const AUTH_HANDSHAKE_PATHS = ['/landlord/auth/refresh', '/landlord/auth/sso/initiate'];

afterResponseHooks.push(async ({ request, response, retryCount }) => {
  if (response.status !== 401) return response;
  if (retryCount > 0) return response;
  if (AUTH_HANDSHAKE_PATHS.some((path) => request.url.includes(path))) return response;

  const result = await refreshLandlordToken();

  if (result === 'recovered') {
    return retryRequest();
  }

  if (result === 'unavailable') {
    return response;
  }

  useLandlordAuthStore().reset();

  return response;
});

const REASON_MESSAGE_KEYS: Record<string, string> = {
  'tenant-access-denied': 'auth.tenant-access-denied',
  'app-open-failed': 'auth.app-open-failed',
  'sign-in-again': 'auth.sign-in-again',
};

router.beforeEach((to) => {
  const messageKey = REASON_MESSAGE_KEYS[to.query.reason as string];

  if (!messageKey) return true;

  message.error(i18n.global.t(messageKey));

  const { reason: _reason, ...query } = to.query;

  return { ...to, query };
});

router.afterEach((to) => {
  document.title = `${to.meta.title} — MijnAfinius`;
});
